import {
  BUILTIN_ATTRIBUTION_RULES,
  CANONICAL_FIELD_KEYS,
  DEFAULT_CAPTURE_QUERY_KEYS,
  DEFAULT_MAX_CAPTURED_QUERY_PARAMS,
  DEFAULT_MAX_QUERY_PARAM_VALUE_LENGTH,
  DEFAULT_QUERY_DENYLIST,
  QUERY_KEY_ALIASES,
  isAttributionField,
} from './catalog';
import type {
  AttributionCaptureOptions,
  AttributionField,
  AttributionParamMap,
  AttributionParamMapEntry,
  AttributionProvider,
  AttributionSnapshot,
  ResolvedAttributionSignal,
} from './types';

const FIELD_PRIORITY: AttributionField[] = [
  'publisher_key',
  'click_id',
  'campaign_id',
  'coupon_code',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'sub_id1',
  'sub_id2',
  'sub_id3',
  'sub_id4',
  'sub_id5',
];

export function normalizeAttributionKey(key: string): string {
  return key.trim().toLowerCase();
}

export function createAttributionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `att_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export function toSearchParams(searchOrUrl: string): URLSearchParams {
  const raw = searchOrUrl.trim();
  if (!raw) return new URLSearchParams();
  try {
    if (/^https?:\/\//i.test(raw) || raw.startsWith('//')) {
      const u = new URL(raw.startsWith('//') ? `https:${raw}` : raw);
      return new URLSearchParams(u.search);
    }
  } catch {
    /* fall through */
  }
  const qIndex = raw.indexOf('?');
  if (qIndex >= 0) {
    return new URLSearchParams(raw.slice(qIndex + 1).split('#')[0] || '');
  }
  if (raw.startsWith('?')) {
    return new URLSearchParams(raw.slice(1).split('#')[0] || '');
  }
  return new URLSearchParams(raw);
}

export function buildNormalizedParamMap(params: URLSearchParams): Record<string, string> {
  const map: Record<string, string> = {};
  for (const [rawKey, rawValue] of params.entries()) {
    const key = normalizeAttributionKey(rawKey);
    if (!key || key in map) continue;
    map[key] = rawValue;
  }
  return map;
}

function normalizeNonNegativeInteger(value: number | undefined, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  const normalized = Math.floor(value);
  return normalized < 0 ? fallback : normalized;
}

function getNormalizedKeys(
  keys: readonly string[] | undefined,
  defaults: readonly string[]
): string[] {
  const source = keys ?? defaults;
  const values = new Set<string>();
  for (const key of source) {
    if (typeof key !== 'string') continue;
    const normalized = normalizeAttributionKey(key);
    if (normalized) values.add(normalized);
  }
  return Array.from(values);
}

function resolveMapEntry(
  entry: AttributionParamMapEntry | AttributionField | undefined
): AttributionParamMapEntry | null {
  if (!entry) return null;
  if (typeof entry === 'string') {
    return isAttributionField(entry) ? { field: entry } : null;
  }
  if (!isAttributionField(entry.field)) return null;
  return {
    field: entry.field,
    ...(entry.provider ? { provider: entry.provider } : {}),
  };
}

function captureRawQuery(
  params: URLSearchParams,
  options: AttributionCaptureOptions,
  mappedKeys: Set<string>
): Record<string, string> {
  const queryParams: Record<string, string> = {};
  const denylist = new Set(getNormalizedKeys(options.query_param_denylist, DEFAULT_QUERY_DENYLIST));
  const maxParams = normalizeNonNegativeInteger(
    options.max_captured_query_params,
    DEFAULT_MAX_CAPTURED_QUERY_PARAMS
  );
  const maxValueLength = normalizeNonNegativeInteger(
    options.max_query_param_value_length,
    DEFAULT_MAX_QUERY_PARAM_VALUE_LENGTH
  );
  const captureAll = options.capture_all_query_params ?? false;
  const allowedKeys = captureAll
    ? null
    : new Set([
        ...DEFAULT_CAPTURE_QUERY_KEYS.map(normalizeAttributionKey),
        ...getNormalizedKeys(options.capture_query_params, []),
        ...mappedKeys,
      ]);

  for (const [rawKey, rawValue] of params.entries()) {
    const key = normalizeAttributionKey(rawKey);
    if (!key) continue;
    const canonicalKey = QUERY_KEY_ALIASES[key] ?? key;
    if (denylist.has(key) || denylist.has(canonicalKey)) continue;
    if (allowedKeys && !allowedKeys.has(key) && !allowedKeys.has(canonicalKey)) continue;

    const value = rawValue.trim();
    if (!value) continue;
    if (!(canonicalKey in queryParams) && Object.keys(queryParams).length >= maxParams) break;
    queryParams[canonicalKey] = value.slice(0, maxValueLength);
  }

  return queryParams;
}

function isDenied(
  key: string,
  denylist: Set<string>,
  canonicalStorageKey?: string
): boolean {
  if (denylist.has(key)) return true;
  if (canonicalStorageKey && denylist.has(canonicalStorageKey)) return true;
  return false;
}

/**
 * Resolve semantic attribution signals with deterministic precedence:
 * 1. explicit canonical URL key
 * 2. integrator attribution_param_map
 * 3. built-in provider rule
 *
 * First non-empty value wins per field. Lower-priority conflicts are recorded
 * without leaking raw values.
 */
export function resolveAttributionSignals(
  paramMap: Record<string, string>,
  options: AttributionCaptureOptions = {}
): { signals: Map<AttributionField, ResolvedAttributionSignal>; conflicts: string[] } {
  const denylist = new Set(getNormalizedKeys(options.query_param_denylist, DEFAULT_QUERY_DENYLIST));
  const signals = new Map<AttributionField, ResolvedAttributionSignal>();
  const conflicts: string[] = [];

  const trySet = (signal: ResolvedAttributionSignal) => {
    const existing = signals.get(signal.field);
    if (!existing) {
      signals.set(signal.field, signal);
      return;
    }
    if (existing.value !== signal.value || existing.provider !== signal.provider) {
      conflicts.push(
        `${signal.field}: ignored ${signal.source}:${signal.sourceKey} (kept ${existing.source}:${existing.sourceKey})`
      );
    }
  };

  // 1. Canonical keys
  for (const field of FIELD_PRIORITY) {
    for (const key of CANONICAL_FIELD_KEYS[field]) {
      const normalizedKey = normalizeAttributionKey(key);
      if (isDenied(normalizedKey, denylist)) continue;
      const raw = paramMap[normalizedKey]?.trim();
      if (!raw) continue;
      trySet({
        field,
        value: raw,
        source: 'canonical',
        sourceKey: normalizedKey,
      });
      break;
    }
  }

  // 2. Integrator mappings
  const map = options.attribution_param_map ?? {};
  for (const [rawKey, rawEntry] of Object.entries(map)) {
    const key = normalizeAttributionKey(rawKey);
    if (!key || isDenied(key, denylist, QUERY_KEY_ALIASES[key])) continue;
    const entry = resolveMapEntry(rawEntry as AttributionParamMap[string]);
    if (!entry) continue;
    const raw = paramMap[key]?.trim();
    if (!raw) continue;
    trySet({
      field: entry.field,
      value: raw,
      provider: entry.provider,
      source: 'mapping',
      sourceKey: key,
    });
  }

  // 3. Built-in rules
  for (const rule of BUILTIN_ATTRIBUTION_RULES) {
    const key = normalizeAttributionKey(rule.key);
    if (!key || isDenied(key, denylist, QUERY_KEY_ALIASES[key])) continue;
    if (rule.requiresAnyOf?.length) {
      const hasCompanion = rule.requiresAnyOf.some((companion) => {
        const companionKey = normalizeAttributionKey(companion);
        return Boolean(paramMap[companionKey]?.trim());
      });
      if (!hasCompanion) continue;
    }
    const raw = paramMap[key]?.trim();
    if (!raw) continue;
    trySet({
      field: rule.field,
      value: raw,
      provider: rule.provider,
      source: 'builtin',
      sourceKey: key,
    });
  }

  return { signals, conflicts };
}

function pickProvider(signals: Map<AttributionField, ResolvedAttributionSignal>): AttributionProvider | undefined {
  const publisher = signals.get('publisher_key');
  if (publisher?.provider) return publisher.provider;
  const click = signals.get('click_id');
  if (click?.provider) return click.provider;
  for (const field of FIELD_PRIORITY) {
    const signal = signals.get(field);
    if (signal?.provider) return signal.provider;
  }
  if (publisher) return 'direct';
  if (click) return undefined;
  return undefined;
}

function hasRecognizedSignal(signals: Map<AttributionField, ResolvedAttributionSignal>): boolean {
  return (
    signals.has('publisher_key') ||
    signals.has('click_id') ||
    signals.has('campaign_id') ||
    signals.has('coupon_code') ||
    signals.has('utm_source') ||
    signals.has('utm_medium') ||
    signals.has('utm_campaign') ||
    signals.has('utm_term') ||
    signals.has('utm_content')
  );
}

/**
 * Pure URL → AttributionSnapshot normalization.
 * Returns null when the URL contains no recognized attribution signal.
 */
export function normalizeUrlAttribution(
  searchOrUrl: string,
  options: AttributionCaptureOptions = {}
): AttributionSnapshot | null {
  const params = toSearchParams(searchOrUrl);
  const paramMap = buildNormalizedParamMap(params);
  const mappedKeys = new Set(
    Object.keys(options.attribution_param_map ?? {}).map(normalizeAttributionKey).filter(Boolean)
  );
  const { signals, conflicts } = resolveAttributionSignals(paramMap, options);
  if (!hasRecognizedSignal(signals)) {
    // Still allow raw-only capture when capture_query_params / capture_all is set
    // without semantic recognition — but that is not a new attribution touch.
    return null;
  }

  const query = captureRawQuery(params, options, mappedKeys);
  const maxValueLength = normalizeNonNegativeInteger(
    options.max_query_param_value_length,
    DEFAULT_MAX_QUERY_PARAM_VALUE_LENGTH
  );

  const truncate = (value: string) => value.slice(0, maxValueLength);
  const get = (field: AttributionField) => {
    const signal = signals.get(field);
    return signal ? truncate(signal.value) : undefined;
  };

  const snapshot: AttributionSnapshot = {
    id: createAttributionId(),
    captured_at: new Date().toISOString(),
    query,
  };

  const provider = pickProvider(signals);
  if (provider) snapshot.provider = provider;

  const publisherKey = get('publisher_key');
  if (publisherKey) snapshot.publisher_key = publisherKey;
  const clickId = get('click_id');
  if (clickId) snapshot.click_id = clickId;
  const campaignId = get('campaign_id');
  if (campaignId) snapshot.campaign_id = campaignId;
  const couponCode = get('coupon_code');
  if (couponCode) snapshot.coupon_code = couponCode;

  for (const utm of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const) {
    const value = get(utm);
    if (value) snapshot[utm] = value;
  }
  for (const sub of ['sub_id1', 'sub_id2', 'sub_id3', 'sub_id4', 'sub_id5'] as const) {
    const value = get(sub);
    if (value) snapshot[sub] = value;
  }

  if (conflicts.length > 0) {
    snapshot.conflicts = conflicts;
    if (options.debug && typeof console !== 'undefined') {
      for (const notice of conflicts) {
        console.warn(`[Mark] attribution conflict: ${notice}`);
      }
    }
  }

  return snapshot;
}

/**
 * Capture allowlisted raw query params even when no semantic signal is present.
 * Does not create an attribution id / snapshot.
 */
export function captureRawQueryParams(
  searchOrUrl: string,
  options: AttributionCaptureOptions = {}
): Record<string, string> {
  const params = toSearchParams(searchOrUrl);
  const mappedKeys = new Set(
    Object.keys(options.attribution_param_map ?? {}).map(normalizeAttributionKey).filter(Boolean)
  );
  return captureRawQuery(params, options, mappedKeys);
}

/**
 * Strip query string and hash; keep origin+pathname for absolute URLs.
 */
export function stripUrlQueryAndHash(urlOrPath: string): string {
  const raw = urlOrPath.trim();
  if (!raw) return raw;
  try {
    if (/^https?:\/\//i.test(raw) || raw.startsWith('//')) {
      const u = new URL(raw.startsWith('//') ? `https:${raw}` : raw);
      return `${u.origin}${u.pathname || '/'}`;
    }
  } catch {
    /* fall through */
  }
  const cut = raw.search(/[?#]/);
  return cut >= 0 ? raw.slice(0, cut) : raw;
}

/**
 * Migrate legacy storage fields into an AttributionSnapshot when possible.
 */
export function migrateLegacyAttributionStorage(data: {
  last_click_id?: string;
  campaign_id?: string;
  query_params?: Record<string, string>;
  attribution?: AttributionSnapshot;
}): AttributionSnapshot | undefined {
  if (data.attribution?.id) {
    return data.attribution;
  }
  const query = data.query_params ?? {};
  const clickId = data.last_click_id;
  const campaignId = data.campaign_id;
  const publisherKey =
    query.olaff ||
    query.ol_aff ||
    query.aw_affid ||
    query.sv_campaign_id ||
    query.irpid ||
    query.irmp ||
    query.via ||
    query.fpr ||
    query.fp_ref ||
    query.rfsn ||
    query.ps_partner_key ||
    query.a_aid ||
    query.mbsy ||
    query.idev ||
    query.publisher_key ||
    query.ref ||
    query.aff ||
    query.per ||
    undefined;
  const couponCode = query.coupon || query.coupon_code;
  if (!clickId && !campaignId && !publisherKey && !couponCode && Object.keys(query).length === 0) {
    return undefined;
  }

  let provider: AttributionProvider | undefined;
  if (query.olaff || query.ol_aff) provider = 'onelence';
  else if (query.aw_affid || query.awc || query.sv_campaign_id) provider = 'awin';
  else if (query.irpid || query.irmp || query.irclickid) provider = 'impact';
  else if (query.cjevent || query.pid) provider = 'cj';
  else if (query.sscid) provider = 'shareasale';
  else if (query.camref) provider = 'partnerize';
  else if (query.ranSiteID) provider = 'rakuten';
  else if (query.tduid) provider = 'tradedoubler';
  else if (query.via) provider = 'rewardful';
  else if (query.fpr || query.fp_ref) provider = 'firstpromoter';
  else if (query.rfsn) provider = 'refersion';
  else if (query.ps_partner_key || query.ps_xid) provider = 'partnerstack';
  else if (query.a_aid) provider = 'postaffiliatepro';
  else if (query.mbsy) provider = 'talkable';
  else if (query.idev) provider = 'idevaffiliate';
  else if (publisherKey) provider = 'direct';

  return {
    id: createAttributionId(),
    captured_at: new Date().toISOString(),
    ...(provider ? { provider } : {}),
    ...(publisherKey ? { publisher_key: publisherKey } : {}),
    ...(clickId ? { click_id: clickId } : {}),
    ...(campaignId ? { campaign_id: campaignId } : {}),
    ...(couponCode ? { coupon_code: couponCode } : {}),
    ...(query.utm_source ? { utm_source: query.utm_source } : {}),
    ...(query.utm_medium ? { utm_medium: query.utm_medium } : {}),
    ...(query.utm_campaign ? { utm_campaign: query.utm_campaign } : {}),
    ...(query.utm_term ? { utm_term: query.utm_term } : {}),
    ...(query.utm_content ? { utm_content: query.utm_content } : {}),
    query,
  };
}
